import { PaletteColor } from '@mui/material';
import { rgba } from 'polished';
import { typographyToCss } from 'shared/lib/mui';
import styled, { css, DefaultTheme } from 'styled-components';

import { Colors, Sizes, Variants } from './types';

const getColor = (color: Colors, theme: DefaultTheme): PaletteColor => {
  switch (color) {
    case Colors.Error:
      return {
        main: theme.palette.error.main,
        dark: theme.palette.error.dark,
        light: theme.palette.error.light,
        contrastText: theme.palette.error.contrastText,
      };
    case Colors.Warning:
      return theme.palette.warning;
    case Colors.Info:
      return {
        main: theme.palette.info.main,
        dark: theme.palette.info.dark,
        light: theme.palette.info.light,
        contrastText: theme.palette.info.contrastText,
      };
    case Colors.Success:
      return theme.palette.success;
    case Colors.White:
      return {
        main: theme.palette.common.white,
        dark: theme.palette.common.white,
        light: theme.palette.common.white,
        contrastText: theme.palette.common.black,
      };
    case Colors.TextPrimary:
      return {
        main: rgba(theme.palette.text.primary, 0.08),
        dark: rgba(theme.palette.text.primary, 0.12),
        light: rgba(theme.palette.text.primary, 0.06),
        contrastText: theme.palette.text.primary,
      };
    case Colors.TextSecondary:
      return {
        main: rgba(theme.palette.text.primary, 0.08),
        dark: rgba(theme.palette.text.primary, 0.12),
        light: rgba(theme.palette.text.primary, 0.06),
        contrastText: theme.palette.text.secondary,
      };
    case Colors.Inherit:
      return {
        main: 'inherit',
        dark: 'inherit',
        light: 'inherit',
        contrastText: 'inherit',
      };
    default:
      return theme.palette.primary;
  }
};

// These carry a translucent wash in main/dark/light, so their label lives in contrastText.
const hasTranslucentSurface = (color: Colors): boolean =>
  color === Colors.TextPrimary || color === Colors.TextSecondary;

const getContainedBackground = (color: Colors, theme: DefaultTheme): string =>
  color === Colors.Inherit
    ? theme.palette.grey[100]
    : getColor(color, theme).main;

const getContainedLightBackground = (
  color: Colors,
  theme: DefaultTheme,
): string => {
  if (color === Colors.Inherit) {
    return theme.palette.grey[100];
  }
  if (color === Colors.Primary) {
    return theme.palette.primaryExtended.opacityBackgroundBackground;
  }
  return getColor(color, theme).light;
};

const getContainedLightLabelColor = (
  color: Colors,
  theme: DefaultTheme,
): string => {
  if (color === Colors.Primary) {
    return theme.palette.primaryExtended.opacityBackgroundText;
  }
  const paletteColor = getColor(color, theme);
  return hasTranslucentSurface(color)
    ? paletteColor.contrastText
    : paletteColor.dark;
};

const getLabelColor = (color: Colors, theme: DefaultTheme): string => {
  const paletteColor = getColor(color, theme);
  return hasTranslucentSurface(color)
    ? paletteColor.contrastText
    : paletteColor.main;
};

const getTextVariantLabelColor = (
  color: Colors,
  theme: DefaultTheme,
): string =>
  color === Colors.Primary
    ? theme.palette.primaryExtended.opacityBackgroundText
    : getLabelColor(color, theme);

const getTextVariantBackground = (
  color: Colors,
  theme: DefaultTheme,
): string => {
  if (color === Colors.Primary) {
    return theme.palette.primaryExtended.opacityBackgroundBackground;
  }
  return rgba(
    color === Colors.Inherit
      ? theme.palette.common.black
      : getColor(color, theme).main,
    0.04,
  );
};

const getSelectedLabelColor = (color: Colors, theme: DefaultTheme): string =>
  color === Colors.TextSecondary
    ? theme.palette.text.primary
    : getTextVariantLabelColor(color, theme);

const getDisabledBackground = (color: Colors, theme: DefaultTheme): string => {
  if (color === Colors.Inherit) {
    return theme.palette.grey[100];
  }
  if (hasTranslucentSurface(color)) {
    return getColor(color, theme).main;
  }
  return rgba(getColor(color, theme).main, 0.4);
};

const ContainedButton = css<{
  $color: Colors;
  $disabledBackgroundOnly: boolean;
}>`
  border: 1px solid transparent;
  background: ${({ $color, theme }) => getContainedBackground($color, theme)};
  transition: background-color 200ms;

  color: ${({ theme, $color }) =>
    $color === Colors.Inherit
      ? 'inherit'
      : getColor($color, theme).contrastText};

  &:hover {
    background: ${({ $color, theme }) =>
      $color === Colors.Inherit
        ? theme.palette.grey['200']
        : getColor($color, theme).dark};
  }

  ${({ $disabledBackgroundOnly, $color, theme }) =>
    $disabledBackgroundOnly &&
    css`
      &:disabled {
        opacity: 1;
        background: ${getDisabledBackground($color, theme)};
      }
    `};
`;

const ContainedLightButton = css<{ $color: Colors }>`
  border: 1px solid transparent;
  background: ${({ $color, theme }) =>
    getContainedLightBackground($color, theme)};
  transition: background-color 200ms;

  color: ${({ $color, theme }) => getContainedLightLabelColor($color, theme)};
`;

const OutlinedButton = css<{
  $color: Colors;
  $disabledBackgroundOnly: boolean;
}>`
  background: transparent;
  border: 1px solid
    ${({ $color, theme }) =>
      $color === Colors.Inherit
        ? theme.palette.grey[400]
        : getColor($color, theme).main};
  transition: background 200ms;

  color: ${({ theme, $color }) => getLabelColor($color, theme)};

  ${({ $disabledBackgroundOnly, $color, theme }) =>
    $disabledBackgroundOnly &&
    css`
      &:disabled {
        opacity: 1;
        border: 1px solid
          ${rgba(
            $color === Colors.Inherit
              ? theme.palette.divider
              : getColor($color, theme).main,
            0.4,
          )};
      }
    `};
`;

const TextButton = css<{ $selected: boolean; $color: Colors }>`
  background: transparent;
  transition: background 200ms;
  border: 1px solid transparent;

  color: ${({ $color, theme }) => getTextVariantLabelColor($color, theme)};

  &:hover {
    background: ${({ $color, theme }) =>
      getTextVariantBackground($color, theme)};
  }

  ${({ $selected, $color, theme }) =>
    $selected &&
    css`
      background: ${getTextVariantBackground($color, theme)};
      color: ${getSelectedLabelColor($color, theme)};
    `};
`;

const LargeButton = css`
  ${({ theme }) => typographyToCss(theme.typography.buttonLarge)};
  padding: ${({ theme }) => `${theme.spacing(2.25)} ${theme.spacing(3.75)}`};
`;

const MediumButton = css`
  ${({ theme }) => typographyToCss(theme.typography.button)};
  padding: ${({ theme }) => `${theme.spacing(1.25)} ${theme.spacing(3)}`};
`;

const SmallButton = css`
  ${({ theme }) => typographyToCss(theme.typography.buttonSmall)};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2.5)}`};
`;

export const Button = styled.button<{
  $color: Colors;
  $variant: Variants;
  $size: Sizes;
  $fullWidth: boolean;
  $alignCenter: boolean;
  $disabledBackgroundOnly: boolean;
  $preventDrag: boolean;
  $selected: boolean;
}>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin: 0;

  border-radius: ${({ theme }) => theme.spacing(8)};
  cursor: pointer;

  text-decoration: none;
  outline: none;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `};

  ${({ $alignCenter }) =>
    $alignCenter &&
    css`
      justify-content: center;
    `};

  ${({ $preventDrag }) =>
    $preventDrag &&
    css`
      -webkit-app-region: no-drag;
    `};

  ${({ $variant }) => {
    switch ($variant) {
      case Variants.Text:
        return TextButton;
      case Variants.Outlined:
        return OutlinedButton;
      case Variants.ContainedLight:
        return ContainedLightButton;
      default:
        return ContainedButton;
    }
  }};

  ${({ $size }) => {
    switch ($size) {
      case Sizes.Medium:
        return MediumButton;
      case Sizes.Small:
        return SmallButton;
      default:
        return LargeButton;
    }
  }}
`;

export const Label = styled.span``;

export const IconContainer = styled.span<{ $size: Sizes }>`
  color: inherit;
  line-height: 62.5%;

  ${({ $size }) => {
    switch ($size) {
      case Sizes.Small:
        return css`
          font-size: 1.6rem;
        `;
      case Sizes.Medium:
        return css`
          font-size: 1.6rem;
        `;
      default:
        return css`
          font-size: 1.6rem;
        `;
    }
  }}
`;
